import type { TodayQueueOutput } from "@/api/tools";

export type TodayRow = TodayQueueOutput["needs_you"][number];

// V1-INTERACTIONS §4.1 / EM-23 — the All view is the outstanding work grouped by the family it belongs to, in this
// order. `row.family` is derived server-side (backend/app/tools/today.py `_family`), so the browser holds no domain
// knowledge and the classification is proved in pytest. A subject type outside the three named families lands in
// `other`, which keeps it on the screen rather than dropping it.
export const FAMILIES = ["interviews", "debriefs", "feedback", "other"] as const;
export type Family = (typeof FAMILIES)[number];

export const FAMILY_LABEL: Record<Family, string> = {
  interviews: "Interviews",
  debriefs: "Debriefs",
  feedback: "Feedback",
  other: "Everything else",
};

export const FAMILY_EMPTY: Record<Family, string> = {
  interviews: "No interview or scorecard work is outstanding.",
  debriefs: "No debrief is upcoming or pending.",
  feedback: "No feedback has been asked of you.",
  other: "Nothing else is outstanding.",
};

const isFamily = (value: string): value is Family => (FAMILIES as readonly string[]).includes(value);

/**
 * §4.1 — the All view's section bodies. Rows arrive already ordered within each queue section; keeping the queues in
 * the order overdue → attention → pending → upcoming puts the work that is late at the top of every family.
 * Only families that actually carry work are returned: the screen never shows more empty state than work (§4.1).
 */
export const byFamily = (rows: TodayRow[]): { family: Family; rows: TodayRow[] }[] =>
  FAMILIES.map(family => ({
    family,
    rows: rows.filter(row => (isFamily(row.family) ? row.family : "other") === family),
  })).filter(group => group.rows.length > 0);
